import { getCartType } from '@/lib/hooks/helpers/getCartType'
import { getEmptyCarts } from '@/lib/hooks/helpers/getEmptyCarts'
import { removeAuthCookie } from '@/lib/utils/shared/cookies/removeAuthCookie'
import type {} from '@redux-devtools/extension' // required for devtools typing
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { deleteCookie } from 'cookies-next'
import { appInitialValues } from './initialValues/appInitialValues'
import { cartInitialValues } from './initialValues/cartInitialValues'
import { checkoutInitialValues } from './initialValues/checkoutInitialValues'
import { cmsInitialValues } from './initialValues/cmsInitialvalues'
import { loadersInitialValues } from './initialValues/loadersInitialValues'
import { orderInitialValues } from './initialValues/orderInitialValues'
import { userInitialValues } from './initialValues/userInitialValues'
import type { AppType } from './types/app'
import type { CartItemType, CartType, PromoKeys } from './types/cart'
import type { CheckoutType } from './types/checkout'
import type { CMSType } from './types/cms'
import type { LoadersType } from './types/loaders'
import type { OrderShippingType, OrderType } from './types/order'
import type { AddedLines, PromoItems, PromoResponse } from './types/promo'
import type { CustomerObjectType, UserType } from './types/user'
import type { QuotesItem } from '../lib/services/Jeeves/pricing/getProductPricing'
import { subscriptionsInitialValues } from './initialValues/subscriptionsInitialValues'
import type { SubscriptionsType } from './types/subscriptions'

export type GlobalStoreValues = {
  app: AppType
  cart: CartType
  cms: CMSType
  user: UserType
  order: OrderType
  subscriptions: SubscriptionsType
  checkout: CheckoutType
  loaders: LoadersType
  overwriteItems: QuotesItem[]
}

export type GlobalStoreSetters = {
  setApp: (app: Partial<AppType>) => void
  setAppAddProductToSubscription: (
    newAddProductToSubscriptionFlow: Partial<
      AppType['addProductToSubscription']
    >,
  ) => void
  setOrderWorldPay: (worldPay: Partial<OrderType['worldPay']>) => void
  addToCart: (cart: CartItemType) => void
  removeFromCart: (cart: CartItemType) => void
  updateCartItemQty: (cart: CartItemType) => void
  handleSwapCartItems: (cart: CartItemType) => void // Swaps from OTP to Subscribe and vice versa
  setCMS: (cart: Partial<CMSType>) => void
  setUser: (user: Partial<UserType>) => void
  setCustomer: (customer: Partial<CustomerObjectType>) => void
  setOrder: (order: Partial<OrderType>) => void
  setOrderShipping: (shipping: Partial<OrderShippingType>) => void
  resetStore: () => void
  resetStoreWithoutTranslations: () => void
  setCheckout: (checkout: Partial<CheckoutType>) => void
  setLoaders: (loaders: Partial<LoadersType>) => void
  setAddedLines: (addedLines: AddedLines) => void
  clearCart: () => void
  setSubscriptions: (subscriptions: Partial<SubscriptionsType>) => void
  setOverwriteItems: (overwriteItems: QuotesItem[]) => void
  setCartPromos: (promoItems: PromoResponse[]) => void
  setLoginPopoverOpen: (isOpen: boolean) => void
}

const initialState: GlobalStoreValues = {
  app: appInitialValues,
  cart: cartInitialValues,
  cms: cmsInitialValues,
  user: userInitialValues,
  order: orderInitialValues,
  checkout: checkoutInitialValues,
  loaders: loadersInitialValues,
  subscriptions: subscriptionsInitialValues,
  overwriteItems: [],
}

// TODO: Implement this when we have a better understanding of how to use it, since it will be useful
// TODO: for utilizing multiple tab for purchasing and synching them when products are added to the cart for example.
// TODO: https://zustand.docs.pmnd.rs/integrations/persisting-store-data#how-can-i-rehydrate-on-storage-event
// type StoreWithPersist<T> = Mutate<StoreApi<T>, [['zustand/persist', unknown]]>

// export const withStorageDomEvents = <T>(store: StoreWithPersist<T>) => {
//   const storageEventCallback = (event: StorageEvent) => {
//     ;(async () => {
//       if (event.key === store.persist.getOptions().name && event.newValue) {
//         await store.persist.rehydrate()
//       }
//     })()
//   }

//   if (typeof window !== 'undefined') {
//     window.addEventListener('storage', storageEventCallback)
//   }

//   return () => {
//     if (typeof window !== 'undefined') {
//       window.removeEventListener('storage', storageEventCallback)
//     }
//   }
// }

const useGlobalStore = create<GlobalStoreValues & GlobalStoreSetters>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // This approach allows us to pass in a partial object, meaning we are able to pass
        // in only what we need to update and the rest of the object will remain unchanged.

        // And makes things much simpler because we don't have to create a setter for each value we want to update

        // In case we need to run some logic before updating the state, we can create a custom setter for that particular value

        setApp: (newApp): void => {
          set((state) => ({
            app: {
              ...state.app,
              ...newApp,
            },
          }))
        },
        setAppAddProductToSubscription: (newAddProductToSubscription): void => {
          set((state) => ({
            app: {
              ...state.app,
              addProductToSubscription: {
                ...state.app.addProductToSubscription,
                ...newAddProductToSubscription,
              },
            },
          }))
        },
        setOrderWorldPay: (newWorldPay): void => {
          set((state) => ({
            order: {
              ...state.order,
              worldPay: {
                ...state.order.worldPay,
                ...newWorldPay,
              },
            },
          }))
        },
        updateCartItemQty: (itemToUpdate) => {
          const sku = itemToUpdate.product.pricing?.sku

          // TODO: Redo logic to handle adding multiple OTPS or SUBS to cart
          // Check if the product is already in the cart
          const { cart } = get()
          const cartType = getCartType(itemToUpdate.po)

          if (cartType !== null) {
            const updatedCart = { ...cart }
            const productIndex = cart[cartType].findIndex(
              (item) => item?.product?.pricing?.sku === sku,
            )
            updatedCart[cartType][productIndex] = {
              ...itemToUpdate,
            }
            set({ cart: updatedCart })
          }
        },
        addToCart: (productData): void => {
          const sku = productData?.product.sku
          const { cart } = get()
          const cartType = getCartType(productData.po)

          if (cartType !== null) {
            const productIndex = cart[cartType].findIndex(
              (item: CartItemType) => item?.product?.pricing?.sku === sku,
            )

            if (productIndex > -1) {
              const updatedCart = { ...cart }
              updatedCart[cartType][productIndex] = {
                ...updatedCart[cartType][productIndex],
                qty: updatedCart[cartType][productIndex].qty + productData.qty,
              }

              if (
                productData.addToCartLocation &&
                productData.addToCartLocation.length === 1
              ) {
                updatedCart[cartType][productIndex] = {
                  ...updatedCart[cartType][productIndex],
                  addToCartLocation: [
                    ...(updatedCart[cartType][productIndex].addToCartLocation ||
                      []),
                    productData.addToCartLocation[0] ||
                      'product_page_main_button',
                  ],
                }
              }

              set({ cart: updatedCart })
            } else {
              // get largest index from otp and subscribe array, 0 added as default index if both arrays are empty
              const lastIndex = Math.max(
                0,
                ...[...cart.otp, ...cart.subscribe].map(
                  (item) => item.index || 0,
                ),
              )

              const newCartItem = {
                ...productData,
                index: lastIndex + 1,
              }
              set((state) => ({
                cart: {
                  ...state.cart,
                  [cartType]: [...state.cart[cartType], newCartItem],
                },
              }))
            }
          }
        },
        removeFromCart: (itemToRemove): void => {
          const sku = itemToRemove.product.pricing?.sku
          const { cart } = get()
          const cartType = getCartType(itemToRemove.po)

          if (cartType !== null) {
            const updatedCart: CartType = { ...cart }

            updatedCart[cartType] = cart[cartType].filter(
              (item) => item?.product?.pricing?.sku !== sku,
            )

            const checkPromosToBeRemoved: PromoKeys[] =
              getEmptyCarts(updatedCart)

            for (const promoArray of checkPromosToBeRemoved) {
              if (promoArray in updatedCart) {
                // Reset the promo array to an empty array of PromoItems. i tried everything to make this line happy
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                updatedCart[promoArray] = [] as PromoItems[]
              }
            }

            // Update the state with the modified cart
            set({ cart: updatedCart })
          }
        },
        handleSwapCartItems: (itemToSwap): void => {
          const { cart } = get()
          const cartType = getCartType(itemToSwap.po)
          const updatedCart = { ...cart }
          const updatedItem = { ...itemToSwap }
          const updatedCartType = cartType === 'otp' ? 'subscribe' : 'otp'

          updatedItem.po = updatedCartType === 'subscribe' ? 'subscribe' : 'otp'
          const itemExists = updatedCart[updatedCartType].some(
            (item) =>
              item.product.pricing?.sku === updatedItem.product.pricing?.sku,
          )
          if (itemExists) {
            updatedCart[updatedCartType] = updatedCart[updatedCartType].map(
              (item) => {
                if (
                  item.product.pricing?.sku === updatedItem.product.pricing?.sku
                ) {
                  return { ...item, qty: item.qty + updatedItem.qty }
                }
                return item
              },
            )
            updatedCart[cartType] = updatedCart[cartType].filter(
              (item) =>
                item?.product?.pricing?.sku !==
                updatedItem.product.pricing?.sku,
            )
          } else {
            updatedCart[updatedCartType] = [
              ...updatedCart[updatedCartType],
              updatedItem,
            ]
            updatedCart[cartType] = updatedCart[cartType].filter(
              (item) =>
                item?.product?.pricing?.sku !==
                updatedItem.product.pricing?.sku,
            )
          }
          set({ cart: updatedCart })
        },

        setOrder: (newOrder): void => {
          set((state) => ({
            order: {
              ...state.order,
              ...newOrder,
            },
          }))
        },
        setOrderShipping: (newShipping): void => {
          set((state) => ({
            order: {
              ...state.order,
              shipping: {
                ...state.order.shipping,
                ...newShipping,
              },
            },
          }))
        },
        setCMS: (newCMS): void => {
          set((state) => ({
            cms: {
              ...state.cms,
              ...newCMS,
            },
          }))
        },
        setUser: (newUser): void => {
          set((state) => ({
            user: {
              ...state.user,
              ...newUser,
            },
          }))
        },
        setCustomer: (newCustomer): void => {
          const oldCustomer = get().user.customer

          if (!oldCustomer) return

          const updatedCustomer = {
            ...oldCustomer,
            ...newCustomer,
          }

          set((state) => ({
            user: {
              ...state.user,
              customer: updatedCustomer,
            },
          }))
        },
        resetStore: (): void => {
          removeAuthCookie({})

          set(() => ({
            ...initialState,
          }))
        },
        resetStoreWithoutTranslations: (): void => {
          // Remove all authentication and user-related cookies
          removeAuthCookie({})

          // Also remove Mixpanel and other user-related cookies
          if (typeof window !== 'undefined') {
            const cookiesToRemove = [
              'userInitilizedInMixpanel',
              'is_user_logged_in',
              'user_type',
              'device_id',
              'initial_referrer',
              'initial_referring_domain',
            ]

            for (const cookieName of cookiesToRemove) {
              deleteCookie(cookieName)
            }
          }

          set((state) => ({
            ...initialState,
            app: {
              ...initialState.app,
              translations: state.app.translations,
            },
            overwriteItems: [],
          }))
        },
        setCheckout: (newCheckout): void => {
          set((state) => ({
            checkout: {
              ...state.checkout,
              ...newCheckout,
            },
          }))
        },
        setLoaders: (newLoaders): void => {
          set((state) => ({
            loaders: {
              ...state.loaders,
              ...newLoaders,
            },
          }))
        },
        setSubscriptions: (newSubscriptions): void => {
          set((state) => ({
            subscriptions: {
              ...state.subscriptions,
              ...newSubscriptions,
            },
          }))
        },
        setAddedLines: (addedLines): void => {
          set((state) => ({
            cart: {
              ...state.cart,
              addedLines,
            },
          }))
        },
        clearCart: (): void => {
          set(() => ({
            cart: { ...initialState.cart },
          }))
        },
        setOverwriteItems: (newOverwriteItems: QuotesItem[]): void => {
          set(() => ({
            overwriteItems: newOverwriteItems,
          }))
        },
        setCartPromos: (promoItems: PromoResponse[]) => {
          set((state) => ({
            cart: {
              ...state.cart,
              promoItems,
            },
          }))
        },
        setLoginPopoverOpen: (isOpen: boolean): void => {
          set((state) => ({
            app: {
              ...state.app,
              isLoginPopoverOpen: isOpen,
            },
          }))
        },
      }),
      {
        name: 'global-store',
        storage: createJSONStorage(() => localStorage),
      },
    ),
  ),
)

export default useGlobalStore
